import { createContext, useContext } from 'react';
import { RouterContext } from 'next/dist/shared/lib/router-context.shared-runtime';
import { useLocale as useNextIntlLocale } from 'next-intl';

/**
 * When set, useLocale() returns this value instead of reading from the router.
 * Use when rendering a subtree without the Next.js Router (e.g. getServerState()
 * for InstantSearch SSR), so useRouter() would throw "NextRouter was not mounted".
 */
export const LocaleOverrideContext = createContext<string | null>(null);

/**
 * Extracts the locale from the router query parameter.
 * With the [locale] dynamic segment pattern, locale is available
 * via router.query.locale instead of router.locale.
 * If LocaleOverrideContext is set (e.g. in getServerState tree), returns that instead.
 *
 * Supports both Pages Router (using RouterContext) and App Router (using next-intl).
 */
export function useLocale(): string {
  const override = useContext(LocaleOverrideContext);
  const pagesRouter = useContext(RouterContext);

  let appRouterLocale: string | undefined;
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    appRouterLocale = useNextIntlLocale();
  } catch {
    appRouterLocale = undefined;
  }

  if (override !== null) return override;

  // Pages Router: extract from router.query.locale
  if (pagesRouter) {
    return (pagesRouter.query.locale as string) || 'en';
  }

  return appRouterLocale || 'en';
}
